/*
 * Copyright (c) Nexign, JSC, 2019-2023.
 */
/**
 * Component for search data from all sources
 *
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @version 1.23.0
 * @since 1.0.0
 */
import type { ChangeEvent, RefObject, KeyboardEvent } from 'react';
import { createRef, PureComponent, Fragment } from 'react';

import debounce from 'lodash/debounce';
import get from 'lodash/get';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { icons } from '@assets/index';
import { Input } from '@components/common/index';
import type { IRootReducer } from '@reducers/rootReducer';

import {
  DEBOUNCE_DEFAULT_DELAY,
  KeyboardKeyEnum,
} from '../../app/appConstants';
import type { IWindowEvent } from '../../app/hoc/WithWindowEvents';
import withWindowEvents from '../../app/hoc/WithWindowEvents';
import { makeSelectTranslationCommon } from '../../internationalization/intSelectors';
import type { ICommonTranslation } from '../../internationalization/types/index';
import { makeSelectGuardComponents } from '../../role';
import { guardComponentType } from '../../role/reducer/guardReducer';
import {
  superSearchChange,
  superSearchClear,
  superSearchStart,
  superSearchToggle,
} from '../superSearchActions';
import {
  makeSelectSuperSearchEmpty,
  makeSelectSuperSearchError,
  makeSelectSuperSearchIsOpen,
  makeSelectSuperSearchIsSearch,
  makeSelectSuperSearchResponse,
  makeSelectSuperSearchValue,
} from '../superSearchSelectors';
import type { ISuperSearch } from '../superSearchTypes';
import {
  SuperSearchContainerStyled,
  SuperSearchIconContainerStyled,
  SuperSearchIconStyled,
  SuperSearchInputStyled,
  SuperSearchStubStyled,
} from './styled/SuperSearchStyled';
import SuperSearchList from './SuperSearchList/SuperSearchList';

/**
 * @description Super search connected props
 * @interface ISuperSearchConnected
 * @property {boolean} emptySearch - empty search result
 * @property {boolean} isSearch - searching flag
 * @property {string} message - error message
 * @property {ISuperSearch} searchResponse - search result
 * @property {string} searchValue - search value
 * @property {ICommonTranslation} translation - current locale
 */
interface ISuperSearchConnected {
  emptySearch: boolean;
  componentsFeature: guardComponentType;
  isOpen: boolean;
  isSearch: boolean;
  message: string;
  searchResponse: ISuperSearch;
  searchValue: string;
  translation: ICommonTranslation;
}

/**
 * @description Super search dispatch props
 * @interface ISuperSearchDispatch
 * @property {() => void} clearSearch - clear search result value
 * @property {(arg: string) => void} searchChange - set search value
 * @property {(payload: ISetFilterPayload) => void} setFilterAccounts - set Accounts filters
 * @property {(payload: ISetFilterPayload) => void} setFilterMobile - set Subscriber filters
 * @property {() => void} startSearch - start search request
 */
interface ISuperSearchDispatch {
  clearSearch: () => void;
  searchChange: (arg: string) => void;
  startSearch: () => void;
  toggleSearch: (p: boolean) => void;
}

/**
 * @description Super search props
 * @interface ISuperSearchProps
 * @property {History} history - root history
 */

type SuperSearchType = ISuperSearchDispatch & ISuperSearchConnected;

export class SuperSearch extends PureComponent<SuperSearchType> {
  componentRef: RefObject<HTMLDivElement> = createRef<HTMLDivElement>();

  startSearchWithDebounce = debounce(
    this.props.startSearch,
    DEBOUNCE_DEFAULT_DELAY,
  );

  keyDownHandler = (event: KeyboardEvent) => {
    if (event.key === KeyboardKeyEnum.ENTER) {
      this.openSearch();
    }
  };
  openSearch = () => {
    if (!this.props.isOpen) {
      this.props.clearSearch();
      this.props.toggleSearch(true);
    }
  };

  closeSearch = () => {
    this.props.toggleSearch(false);
  };

  startSearchDebounce = () => {
    this.startSearchWithDebounce();
  };

  inputHandler = (event: ChangeEvent<HTMLInputElement>): void => {
    const value = get(event, 'target.value', '');
    this.props.searchChange(value);
    this.startSearchDebounce();
  };

  /**
   * Check outside click, and close list if true
   * @param event
   */
  handleClickOutside = (event: MouseEvent): void => {
    if (
      this.componentRef.current &&
      !this.componentRef.current.contains(event.target as Node) &&
      this.props.isOpen
    ) {
      this.closeSearch();
    }
  };

  /**
   * Method apply select and close list, after key Esc press
   * @param event
   */
  windowKeyDownHandler = (event: KeyboardEvent): void => {
    if (event.key === KeyboardKeyEnum.ESCAPE && this.props.isOpen) {
      this.closeSearch();
    }
  };

  render() {
    return (
      <Fragment>
        <div style={{ width: '56px' }} />
        <SuperSearchContainerStyled
          className={this.props.isOpen ? 'opened' : 'closed'}
          ref={this.componentRef}
        >
          <SuperSearchStubStyled />
          <SuperSearchIconContainerStyled>
            <SuperSearchIconStyled
              src={icons.searchMediumIcon}
              onClick={this.openSearch}
            />
          </SuperSearchIconContainerStyled>
          <SuperSearchInputStyled onKeyDown={this.keyDownHandler}>
            <Input
              autoFocus={this.props.isOpen}
              data-input="inputSuperSearch"
              placeholder={this.props.translation.superSearch.found}
              righticon={icons.cancelIcon}
              righticonhandler={this.closeSearch}
              value={this.props.searchValue}
              onChange={this.inputHandler}
            />
            {this.props.isOpen && (
              <SuperSearchList
                closeSearch={this.closeSearch}
                componentsFeature={this.props.componentsFeature}
                emptySearch={this.props.emptySearch}
                isSearch={this.props.isSearch}
                message={this.props.message}
                searchResult={this.props.searchResponse}
                searchValue={this.props.searchValue}
                startSearch={this.props.startSearch}
                translation={this.props.translation.superSearch}
              />
            )}
          </SuperSearchInputStyled>
        </SuperSearchContainerStyled>
      </Fragment>
    );
  }
}

const mapStateToProps = createStructuredSelector<
  IRootReducer,
  ISuperSearchConnected
>({
  emptySearch: makeSelectSuperSearchEmpty(),
  componentsFeature: makeSelectGuardComponents(),
  isOpen: makeSelectSuperSearchIsOpen(),
  isSearch: makeSelectSuperSearchIsSearch(),
  message: makeSelectSuperSearchError(),
  searchResponse: makeSelectSuperSearchResponse(),
  searchValue: makeSelectSuperSearchValue(),
  translation: makeSelectTranslationCommon(),
});

export const mapDispatchToProps = (dispatch: Dispatch) => ({
  clearSearch: () => dispatch(superSearchClear()),
  searchChange: (payload: string) => dispatch(superSearchChange(payload)),
  startSearch: () => dispatch(superSearchStart()),
  toggleSearch: (p: boolean) => dispatch(superSearchToggle(p)),
});

const superSearchEvents: IWindowEvent[] = [
  {
    event: 'mousedown',
    handler: 'handleClickOutside',
  },
  {
    event: 'keydown',
    handler: 'windowKeyDownHandler',
  },
];

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withWindowEvents(SuperSearch, superSearchEvents));
